"use client";

/**
 * <FormModal> — zajednička modalna komponenta za sve CREATE/EDIT forme u Grozd V3.
 *
 * PRAVILO 18 (CLAUDE.md): široki modal je default — sidebar drawer je zabranjen
 * za CREATE/EDIT (samo za detail i filter panele).
 *
 * Ključne karakteristike:
 *   - 3 širine (md=720, lg=920, xl=1080) + responsive (full-screen na <640px)
 *   - Sticky header (naslov + close X) i sticky footer (lijevo Otkaži, desno primary)
 *   - ESC i klik na backdrop zatvaraju modal — uz "unsaved changes" upozorenje ako je `isDirty`
 *   - Focus trap unutar modala (Tab cycle), auto-focus na prvi input
 *   - Body scroll lock dok je modal otvoren
 *   - Fade + scale animacija (CSS-only, bez biblioteke)
 *   - CSS varijable only (var(--surface-card), var(--ink), var(--n100))
 *
 * Za detalje: docs/skilovi/frontend/09-SKILL-formmodal.md
 */

import * as React from "react";
import { createPortal } from "react-dom";

export type FormModalWidth = "md" | "lg" | "xl";

const WIDTH_PX: Record<FormModalWidth, number> = {
  md: 720,
  lg: 920,
  xl: 1080,
};

export interface FormModalProps {
  /** Da li je modal otvoren. */
  open: boolean;
  /** Poziva se kad korisnik traži zatvaranje (ESC, backdrop, close X). */
  onClose: () => void;
  /** Naslov u sticky header-u (Manrope 800, 18px). */
  title: string;
  /** Opciono: kratak podnaslov ispod title-a (12.5px). */
  description?: string;
  /** Širina modala — md (720), lg (920), xl (1080). Default: md. */
  width?: FormModalWidth;
  /** Sadržaj forme (inputi, sekcije, ...). */
  children: React.ReactNode;
  /** Sadržaj sticky footer-a (dugmad). Ako izostavljeno, footer se ne renderuje. */
  footer?: React.ReactNode;
  /** Ako true, klik na backdrop ili ESC pita za potvrdu prije zatvaranja. */
  isDirty?: boolean;
  /** Custom poruka u confirm dijalogu. Default: "Imate nesačuvane izmjene. Stvarno odustati?". */
  dirtyConfirmMessage?: string;
  /** Custom aria-label za close dugme. Default: "Zatvori". */
  closeAriaLabel?: string;
}

const FOCUSABLE_SELECTOR =
  'button:not([disabled]), [href], input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Vraća sve fokusabilne elemente unutar kontejnera (uključujući one bez `tabindex`).
 */
function getFocusable(container: HTMLElement | null): HTMLElement[] {
  if (!container) return [];
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
    (el) => !el.hasAttribute("data-focus-trap-sentinel"),
  );
}

export function FormModal({
  open,
  onClose,
  title,
  description,
  width = "md",
  children,
  footer,
  isDirty = false,
  dirtyConfirmMessage = "Imate nesačuvane izmjene. Stvarno odustati?",
  closeAriaLabel = "Zatvori",
}: FormModalProps) {
  const dialogRef = React.useRef<HTMLDivElement | null>(null);
  const previousFocusRef = React.useRef<HTMLElement | null>(null);
  const [mounted, setMounted] = React.useState(false);
  const [animateIn, setAnimateIn] = React.useState(false);

  // Velibor 2026-05-07: window.confirm() je ZABRANJEN — koristi se naš
  // dizajn-friendly inline confirm overlay umjesto browser native popup-a.
  const [confirmDirtyOpen, setConfirmDirtyOpen] = React.useState(false);

  // Tražimo potvrdu ako je forma "prljava" (custom UI, ne window.confirm).
  const requestClose = React.useCallback(() => {
    if (isDirty) {
      setConfirmDirtyOpen(true);
      return;
    }
    onClose();
  }, [isDirty, onClose]);

  const confirmDirtyDiscard = React.useCallback(() => {
    setConfirmDirtyOpen(false);
    onClose();
  }, [onClose]);

  const confirmDirtyKeep = React.useCallback(() => {
    setConfirmDirtyOpen(false);
  }, []);

  // Mount/unmount + animacija (fade + scale).
  React.useEffect(() => {
    if (open) {
      setMounted(true);
      // Sledeci frame da animacija krene od 0 → 1
      const id = requestAnimationFrame(() => setAnimateIn(true));
      return () => cancelAnimationFrame(id);
    } else {
      setAnimateIn(false);
      const t = setTimeout(() => setMounted(false), 180);
      return () => clearTimeout(t);
    }
  }, [open]);

  // Body scroll lock + restore previous focus.
  React.useEffect(() => {
    if (!open || typeof document === "undefined") return;
    previousFocusRef.current =
      (document.activeElement as HTMLElement | null) ?? null;
    const prevOverflow = document.documentElement.style.overflow;
    document.documentElement.style.overflow = "hidden";
    return () => {
      document.documentElement.style.overflow = prevOverflow;
      if (previousFocusRef.current && typeof previousFocusRef.current.focus === "function") {
        try {
          previousFocusRef.current.focus();
        } catch {
          /* noop */
        }
      }
    };
  }, [open]);

  // Auto-focus prvi fokusabilni element.
  React.useEffect(() => {
    if (!open || !mounted) return;
    const t = setTimeout(() => {
      const focusable = getFocusable(dialogRef.current);
      // Preferiramo input/textarea/select prije dugmadi.
      const firstInput = focusable.find(
        (el) =>
          el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.tagName === "SELECT",
      );
      const target = firstInput ?? focusable[0];
      target?.focus();
    }, 50);
    return () => clearTimeout(t);
  }, [open, mounted]);

  // ESC + focus trap.
  React.useEffect(() => {
    if (!open) return;
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.stopPropagation();
        requestClose();
        return;
      }
      if (e.key === "Tab") {
        const focusable = getFocusable(dialogRef.current);
        if (focusable.length === 0) {
          e.preventDefault();
          return;
        }
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        const active = document.activeElement as HTMLElement | null;
        if (e.shiftKey) {
          if (active === first || !dialogRef.current?.contains(active)) {
            e.preventDefault();
            last.focus();
          }
        } else {
          if (active === last) {
            e.preventDefault();
            first.focus();
          }
        }
      }
    }
    document.addEventListener("keydown", onKeyDown, true);
    return () => document.removeEventListener("keydown", onKeyDown, true);
  }, [open, requestClose]);

  if (!mounted) return null;
  if (typeof document === "undefined") return null;

  const widthPx = WIDTH_PX[width];

  // Velibor 2026-05-05: portal u document.body — bez ovoga AdminTopbar
  // (sticky top-0 z-20) pravi novi stacking context koji se pojavljuje
  // ISPRED backdrop-a. Korisnik vidi bijelu liniju u vrhu jer blur ne
  // pokriva topbar. Portal eskapira sve parent stacking context-e.
  // zIndex 1000 da bude iznad svega — sidebar (z-30), topbar (z-20),
  // dropdown meniji (z-50). Standardno za Grozd modale.
  const modalContent = (
    <div
      role="presentation"
      onMouseDown={(e) => {
        // Zatvori samo ako je klik direktno na backdrop (ne unutar modala).
        if (e.target === e.currentTarget) requestClose();
      }}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "16px",
        background: "rgba(0,0,0,0.5)",
        backdropFilter: "blur(6px)",
        WebkitBackdropFilter: "blur(6px)",
        opacity: animateIn ? 1 : 0,
        transition: "opacity 180ms cubic-bezier(.22,1,.36,1)",
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        style={{
          background: "var(--surface-card)",
          color: "var(--ink)",
          border: "1px solid var(--n100)",
          borderRadius: 16,
          boxShadow: "0 24px 64px rgba(0,0,0,0.24)",
          width: "100%",
          maxWidth: widthPx,
          maxHeight: "85vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          transform: animateIn ? "scale(1)" : "scale(0.96)",
          opacity: animateIn ? 1 : 0,
          transition:
            "opacity 180ms cubic-bezier(.22,1,.36,1), transform 180ms cubic-bezier(.22,1,.36,1)",
        }}
        className="grozd-form-modal"
      >
        {/* Sticky header */}
        <div
          style={{
            position: "sticky",
            top: 0,
            zIndex: 1,
            background: "var(--surface-card)",
            borderBottom: "1px solid var(--n100)",
            padding: "16px 20px",
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 12,
            flexShrink: 0,
          }}
        >
          <div style={{ minWidth: 0, flex: 1 }}>
            <h2
              style={{
                margin: 0,
                fontFamily:
                  'Manrope, ui-sans-serif, system-ui, -apple-system, sans-serif',
                fontWeight: 800,
                fontSize: 18,
                lineHeight: 1.3,
                color: "var(--ink)",
              }}
            >
              {title}
            </h2>
            {description ? (
              <p
                style={{
                  margin: "4px 0 0",
                  fontSize: 12.5,
                  color: "var(--n400)",
                  lineHeight: 1.45,
                }}
              >
                {description}
              </p>
            ) : null}
          </div>
          <button
            type="button"
            onClick={requestClose}
            aria-label={closeAriaLabel}
            style={{
              flexShrink: 0,
              width: 32,
              height: 32,
              borderRadius: 8,
              border: "1px solid var(--n100)",
              background: "transparent",
              color: "var(--n600)",
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 0,
              transition: "background 120ms ease",
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLButtonElement).style.background = "var(--n50)";
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLButtonElement).style.background = "transparent";
            }}
          >
            {/* Inline X SVG (ne ovisimo o lucide-react ovdje) */}
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M18 6 6 18" />
              <path d="m6 6 12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div
          style={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            padding: "24px",
          }}
        >
          {children}
        </div>

        {/* Sticky footer */}
        {footer ? (
          <div
            style={{
              position: "sticky",
              bottom: 0,
              zIndex: 1,
              background: "var(--surface-card)",
              borderTop: "1px solid var(--n100)",
              padding: "14px 20px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
              flexShrink: 0,
            }}
          >
            {footer}
          </div>
        ) : null}
      </div>

      {/* Velibor 2026-05-07: custom unsaved-changes confirm (umjesto window.confirm).
          Renderuje se kao OVERLAY iznad glavnog modal sadržaja. */}
      {confirmDirtyOpen ? (
        <div
          role="alertdialog"
          aria-modal="true"
          aria-label={dirtyConfirmMessage}
          style={{
            position: "absolute",
            inset: 0,
            zIndex: 10,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "rgba(10, 8, 20, 0.65)",
            backdropFilter: "blur(2px)",
            padding: 16,
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) confirmDirtyKeep();
          }}
        >
          <div
            style={{
              maxWidth: 420,
              width: "100%",
              padding: "20px 22px",
              borderRadius: 14,
              background: "var(--surface-card)",
              border: "1px solid var(--n100)",
              boxShadow: "0 16px 48px rgba(0,0,0,0.32)",
            }}
          >
            <div style={{ display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 14 }}>
              <span
                style={{
                  flexShrink: 0,
                  width: 36,
                  height: 36,
                  borderRadius: 999,
                  background: "rgba(251, 191, 36, 0.15)",
                  border: "1.5px solid rgba(251, 191, 36, 0.5)",
                  color: "#FBBF24",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 20,
                  fontWeight: 800,
                }}
                aria-hidden
              >!</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <h3
                  style={{
                    margin: 0,
                    fontFamily: "Manrope, ui-sans-serif, system-ui, sans-serif",
                    fontWeight: 800,
                    fontSize: 16,
                    color: "var(--ink)",
                  }}
                >
                  Nesačuvane izmjene
                </h3>
                <p
                  style={{
                    margin: "6px 0 0",
                    fontSize: 13,
                    color: "var(--n600)",
                    lineHeight: 1.5,
                  }}
                >
                  {dirtyConfirmMessage}
                </p>
              </div>
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button
                type="button"
                onClick={confirmDirtyKeep}
                autoFocus
                style={{
                  height: 36,
                  padding: "0 14px",
                  borderRadius: 8,
                  background: "transparent",
                  border: "1px solid var(--n100)",
                  color: "var(--ink)",
                  fontFamily: "Manrope, ui-sans-serif, system-ui, sans-serif",
                  fontWeight: 600,
                  fontSize: 13,
                  cursor: "pointer",
                }}
              >
                Zadrži (nastavi unos)
              </button>
              <button
                type="button"
                onClick={confirmDirtyDiscard}
                style={{
                  height: 36,
                  padding: "0 14px",
                  borderRadius: 8,
                  background: "var(--danger, #dc2626)",
                  border: "none",
                  color: "#fff",
                  fontFamily: "Manrope, ui-sans-serif, system-ui, sans-serif",
                  fontWeight: 700,
                  fontSize: 13,
                  cursor: "pointer",
                }}
              >
                Odustani (izgubi izmjene)
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* Mobile (< 640px): full-screen modal */}
      <style>{`
        @media (max-width: 640px) {
          .grozd-form-modal {
            max-width: 100% !important;
            max-height: 100vh !important;
            height: 100vh !important;
            border-radius: 0 !important;
            border: none !important;
          }
        }
      `}</style>
    </div>
  );

  return createPortal(modalContent, document.body);
}
